
import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import LocationHistoryMap from '@/components/bikes/LocationHistoryMap';
import { Loader2, ArrowLeft } from 'lucide-react';
import { toast } from '@/components/ui/sonner';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';
import { loadGoogleMapsForMap } from '@/components/couriers/map/mapService';
import { getCourierByPhone } from '@/utils/supabase/courier';

const CourierLocationHistory: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [courierId, setCourierId] = useState<string | null>(null);
  const [bikeId, setBikeId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [apiLoaded, setApiLoaded] = useState(false);
  const instanceId = useRef(`courier-history-${Date.now()}-${Math.random().toString(36).substring(2, 7)}`);
  const mapKey = useRef(`map-${Date.now()}-${Math.random().toString(36).substring(2, 7)}`);
  
  // Предзагрузка Google Maps API
  useEffect(() => {
    // Проверяем, загружен ли API
    if (window.google && window.google.maps) {
      setApiLoaded(true);
      return;
    }
    
    loadGoogleMapsForMap(
      () => {
        setApiLoaded(true);
      },
      (error) => {
        console.error('Ошибка загрузки Google Maps API:', error);
        toast.error(`Ошибка загрузки карты: ${error}`);
      }
    );
  }, []);
  
  useEffect(() => {
    const fetchCourierData = async () => {
      if (!user) return;
      
      setIsLoading(true);
      
      try {
        // Получаем курьера по телефону
        if (user.phone) {
          console.log('Поиск курьера по телефону:', user.phone);
          const { data: courierData, error } = await getCourierByPhone(user.phone);
          
          if (error) {
            console.error('Ошибка при поиске курьера:', error);
            toast.error('Не удалось загрузить данные курьера');
            setIsLoading(false);
            return;
          }
          
          if (courierData) {
            const courierId = courierData.id;
            console.log('Найден курьер с ID из таблицы couriers:', courierId);
            setCourierId(courierId);
            
            // Получаем назначенный велосипед
            await fetchAssignedBike(courierId);
          } else {
            console.log('Курьер не найден по телефону');
            toast.error('Курьер не найден');
          }
        } else {
          console.log('У пользователя нет телефона');
          toast.error('Необходим номер телефона для поиска курьера');
        }
      } catch (err) {
        console.error('Ошибка при загрузке данных курьера:', err);
        toast.error('Не удалось загрузить данные курьера');
      } finally {
        setIsLoading(false);
      }
    };
    
    const fetchAssignedBike = async (id: string) => {
      try {
        const { data, error } = await supabase
          .from('bikes')
          .select('id')
          .eq('assigned_to', id);
        
        if (error) {
          console.error('Ошибка при получении назначенного велосипеда:', error);
          return;
        }
        
        if (data && data.length > 0) {
          console.log('Найден велосипед, назначенный курьеру:', data[0].id);
          setBikeId(data[0].id);
        } else {
          console.log('Нет велосипеда, назначенного курьеру');
        }
      } catch (err) {
        console.error('Ошибка при запросе назначенного велосипеда:', err);
      }
    };
    
    fetchCourierData();
  }, [user]);
  
  const handleBack = () => {
    navigate('/courier');
  };
  
  const refreshMap = () => {
    // Обновляем ключ карты для принудительного перерендера
    mapKey.current = `map-${Date.now()}-${Math.random().toString(36).substring(2, 7)}`;
  };
  
  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-full py-12">
        <Loader2 className="h-8 w-8 animate-spin mr-2" />
        <span>Загрузка данных курьера...</span>
      </div>
    );
  }
  
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-2xl font-bold">История перемещений</h1>
        <Button
          variant="outline"
          className="flex items-center gap-1"
          onClick={handleBack}
        >
          <ArrowLeft className="h-4 w-4" />
          <span>Назад</span>
        </Button>
      </div>
      
      {!apiLoaded && (
        <div className="bg-muted/20 p-4 rounded-lg flex items-center justify-center">
          <Loader2 className="h-4 w-4 animate-spin mr-2" />
          <span className="text-muted-foreground">Загрузка Google Maps API...</span>
        </div>
      )}
      
      {bikeId && apiLoaded ? (
        <LocationHistoryMap 
          bikeId={bikeId} 
          isAdmin={true}
          key={`courier-bike-${bikeId}-${instanceId.current}-${mapKey.current}`} 
        />
      ) : courierId && apiLoaded ? (
        <div className="p-6 bg-card rounded-lg shadow text-center">
          <p className="text-muted-foreground">
            У вас нет назначенного велосипеда. История перемещений недоступна.
          </p>
          <div className="mt-4">
            <Button variant="outline" onClick={refreshMap}>
              Проверить снова
            </Button>
          </div>
        </div>
      ) : (
        <p className="text-muted-foreground">
          Не удалось определить ID курьера. Пожалуйста, обратитесь к администратору.
        </p>
      )}
    </div>
  );
};

export default CourierLocationHistory;
